import React, { useEffect } from "react";
import { AlertTriangle, Download, FileSpreadsheet, ShieldCheck, Route } from "lucide-react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import { StatCard } from "../components/StatCard";
import { formatDateTime } from "../lib/utils";

export default function ReportsPage() {
  const { reports, loadReports, downloadReport } = useData();

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const totals = reports?.totals || {};

  const handleDownload = async (type) => {
    const result = await downloadReport(type);
    if (result.success) toast.success("Report download started.");
    else toast.error(result.error);
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <StatCard label="Total trips" value={totals.trips || 0} icon={Route} accent="blue" helper="All trips stored in the database" />
        <StatCard label="Completed" value={totals.completed || 0} icon={ShieldCheck} accent="emerald" helper="Successfully finished journeys" />
        <StatCard label="Active" value={totals.in_progress || 0} icon={FileSpreadsheet} accent="amber" helper="Trips currently in progress" />
        <StatCard label="Incidents" value={totals.incidents || 0} icon={AlertTriangle} accent="rose" helper={`Completion rate ${totals.completion_rate || 0}%`} />
        <StatCard label="Handovers" value={totals.handover_verifications || 0} icon={ShieldCheck} accent="slate" helper={`${totals.notifications || 0} notification logs`} />
      </div>

      <div className="guardian-card bg-white p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Council-ready exports</h2>
            <p className="mt-1 text-sm text-[#64748B]">Download clean CSV reports for trips, incidents, and handover verification records.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => handleDownload("trips")} className="inline-flex items-center gap-2 rounded-xl bg-[#163D6B] px-4 py-3 text-sm font-semibold text-white"><Download size={16} /> Trip report</button>
            <button onClick={() => handleDownload("incidents")} className="inline-flex items-center gap-2 rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm font-semibold text-[#0F172A]"><Download size={16} /> Incident report</button>
            <button onClick={() => handleDownload("handovers")} className="inline-flex items-center gap-2 rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm font-semibold text-[#0F172A]"><Download size={16} /> Handover report</button>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="guardian-card bg-white p-6">
          <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Recent handover verifications</h2>
          <div className="mt-4 space-y-3">
            {reports?.recent_handover_verifications?.length ? (
              reports.recent_handover_verifications.map((handover) => (
                <div key={handover.id} className="rounded-2xl border border-[#E2E8F0] p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-medium text-[#0F172A]">{handover.child_name}</p>
                      <p className="mt-1 text-sm text-[#64748B]">Received by {handover.verified_by?.name} ({handover.verified_by?.role})</p>
                      <p className="text-sm text-[#64748B]">Confirmed by {handover.confirmed_by_name || "CareFleet user"} via {handover.method}</p>
                    </div>
                    <p className="text-xs text-[#64748B]">{formatDateTime(handover.timestamp)}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-[#CBD5E1] p-4 text-sm text-[#64748B]">No handover verifications recorded yet.</div>
            )}
          </div>
        </div>

        <div className="guardian-card bg-white p-6">
          <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Recent incidents</h2>
          <div className="mt-4 space-y-3">
            {reports?.recent_incidents?.length ? (
              reports.recent_incidents.map((incident) => (
                <div key={incident.id} className="rounded-2xl border border-[#E2E8F0] p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-medium text-[#0F172A]">{incident.description}</p>
                      <p className="mt-1 text-xs text-[#64748B]">{formatDateTime(incident.timestamp)}</p>
                    </div>
                    <span className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-rose-700">{incident.severity}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-[#CBD5E1] p-4 text-sm text-[#64748B]">No incidents recorded yet.</div>
            )}
          </div>
        </div>
      </div>

      <div className="guardian-card overflow-hidden bg-white">
        <div className="border-b border-[#E2E8F0] px-6 py-4">
          <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Driver compliance summary</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#F8FAFC] text-left text-[#64748B]">
              <tr>
                <th className="px-6 py-3 font-medium">Driver</th>
                <th className="px-6 py-3 font-medium">Trips</th>
                <th className="px-6 py-3 font-medium">Completed</th>
                <th className="px-6 py-3 font-medium">Delayed</th>
                <th className="px-6 py-3 font-medium">Missed</th>
                <th className="px-6 py-3 font-medium">Incidents</th>
                <th className="px-6 py-3 font-medium">Handovers</th>
              </tr>
            </thead>
            <tbody>
              {reports?.driver_compliance?.length ? (
                reports.driver_compliance.map((driver) => (
                  <tr key={driver.driver_id} className="border-t border-[#E2E8F0]">
                    <td className="px-6 py-4 font-medium text-[#0F172A]">{driver.driver_name}</td>
                    <td className="px-6 py-4 text-[#475569]">{driver.total_trips}</td>
                    <td className="px-6 py-4 text-[#475569]">{driver.completed_trips}</td>
                    <td className="px-6 py-4 text-[#475569]">{driver.delayed_trips}</td>
                    <td className="px-6 py-4 text-[#475569]">{driver.missed_trips}</td>
                    <td className="px-6 py-4 text-[#475569]">{driver.incident_count}</td>
                    <td className="px-6 py-4 text-[#475569]">{driver.handover_count}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-[#64748B]">No compliance data available yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
